import { Component, DestroyRef, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActionCommandService } from 'src/app/services/action-command.service';

export interface NotificationBellViewModel {
  currentId: string | null;
  historyIds: string[];
  pendingSend: boolean;
}

// how long the panel stays open after the current result finishes, before
// closing itself
const AUTO_CLOSE_DELAY_MS = 3000;

// Self-contained bell trigger + popover for the navbar. Deliberately doesn't
// use ngbDropdown/.dropdown-menu — Bootstrap forces a white background-color
// on .dropdown-menu, which fought with the dark panel we want here. Shows
// only the most recent tracked command (e.g. a "Run task" run) by default,
// with the rest of the history tucked behind a toggle.
@Component({
    selector: 'app-notification-bell',
    templateUrl: './notification-bell.component.html',
    styleUrls: ['./notification-bell.component.scss'],
    standalone: false
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @Input() newCommandsCount: number = 0;

  public isOpen: boolean = false;
  public historyExpanded: boolean = false;
  // currentId is null while a send is in flight (requestOpen clears it) so
  // the previous command's "Completed" state never flashes on open — the
  // previous item still shows up in historyIds rather than disappearing
  public viewModel$: Observable<NotificationBellViewModel>;

  private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private actionCommandService: ActionCommandService,
    private destroyRef: DestroyRef
  ) {
    this.viewModel$ = combineLatest([
      actionCommandService.trackedMessageIds$,
      actionCommandService.currentMessageId$,
      actionCommandService.pendingSend$
    ]).pipe(
      map(([trackedMessageIds, currentId, pendingSend]) => ({
        currentId,
        historyIds: trackedMessageIds.filter((id) => id !== currentId),
        pendingSend
      }))
    );
  }

  ngOnInit(): void {
    // opens immediately when a command is about to be sent (e.g. "Run task"
    // clicked) — don't wait for the send to round-trip first
    this.actionCommandService.openRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearAutoCloseTimer();
        this.historyExpanded = false;
        this.isOpen = true;
      });

    // safety net for any caller that tracks a message without requesting
    // open first — not on the initial page-load restore of past history
    this.actionCommandService.commandTracked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearAutoCloseTimer();
        this.historyExpanded = false;
        this.isOpen = true;
      });
  }

  ngOnDestroy(): void {
    this.clearAutoCloseTimer();
  }

  public toggleOpen(): void {
    this.isOpen = !this.isOpen;
    this.clearAutoCloseTimer();
  }

  public toggleHistory(): void {
    this.historyExpanded = !this.historyExpanded;
  }

  // bound to the current (latest) result's (completed) output — closes the
  // panel a few seconds after that result finishes successfully
  public scheduleAutoClose(): void {
    this.clearAutoCloseTimer();
    this.autoCloseTimer = setTimeout(() => {
      this.isOpen = false;
      this.autoCloseTimer = null;
    }, AUTO_CLOSE_DELAY_MS);
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }
}
